/**
 * Сообщение о поломке в номере («Облака» #31, часть 3).
 *
 * Поток: создать заявку категории «maintenance» (createServiceRequest) →
 * прикрепить фото поломки первым комментарием с attachments
 * (addRequestComment) — фото уже загружены в MinIO через presign
 * (usePhotoUpload), бэкенд POST /v1/requests фото на создании не принимает,
 * поэтому evidence уходит через штатный механизм вложений комментария.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addRequestComment, createServiceRequest } from '@hsc/api-client';
import {
  ServiceCategoryId,
  type RequestCommentAttachment,
  type ServiceRequestCreatedResponse,
} from '@hsc/types';

export interface MaintenanceReportInput {
  /** Catalog-категория «maintenance», разрезолвленная из витрины. */
  categoryId: string;
  /** Что сломалось — обязательное описание. */
  description: string;
  /** Срочная заявка (например, прорвало воду / нет света). */
  urgent: boolean;
  /** Объектные ключи фото поломки (MinIO), 0..3. */
  photoKeys: readonly string[];
}

const MAINTENANCE_TITLE = 'Поломка в номере';

/**
 * Создаёт заявку о поломке и (если есть фото) прикрепляет их первым
 * комментарием. Возвращает созданную заявку; ошибка вложения фото НЕ
 * валит заявку (она уже создана) — её прокидываем отдельным флагом.
 */
export function useMaintenanceReport() {
  const queryClient = useQueryClient();
  return useMutation<
    ServiceRequestCreatedResponse,
    Error,
    MaintenanceReportInput
  >({
    mutationFn: async (input) => {
      const created = await createServiceRequest({
        category_id: ServiceCategoryId(input.categoryId),
        title: MAINTENANCE_TITLE,
        description: input.description.trim(),
        ...(input.urgent && { priority: 'urgent' }),
      });
      if (input.photoKeys.length > 0) {
        const attachments: RequestCommentAttachment[] = input.photoKeys.map((key) => ({
          key,
        }));
        await addRequestComment(created.id, {
          body: 'Фото поломки',
          attachments,
        });
      }
      return created;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}
